import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Leaf, UtensilsCrossed, Home, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";

/**
 * ExplorePaths — story-first discovery entry.
 *
 * Instead of "what do you want to filter?" → "what do you want to experience?"
 *
 * Each path sets mode + tags to guide the user into a curated flow
 * while reusing the existing explore pipeline.
 *
 * Max 4 paths (per design SoT).
 */

interface PathOption {
  key: string;
  label: string;
  description: string;
  icon: React.ElementType;
  /** Explore mode to set */
  mode: "places" | "do" | "farms";
  /** Tags to apply (optional) */
  tags?: string[];
}

const PATHS: PathOption[] = [
  {
    key: "nature",
    label: "Lugn & natur",
    description: "Platser att andas ut på",
    icon: Leaf,
    mode: "places",
    tags: ["natur"],
  },
  {
    key: "food",
    label: "Mat & gårdsbutik",
    description: "Handla och smaka lokalt",
    icon: UtensilsCrossed,
    mode: "farms",
    tags: [],
  },
  {
    key: "stay",
    label: "Boende & övernattning",
    description: "Sov nära naturen",
    icon: Home,
    mode: "places",
    tags: ["boende"],
  },
  {
    key: "do",
    label: "Aktiviteter & upplevelser",
    description: "Saker att göra och uppleva",
    icon: Sparkles,
    mode: "do",
    tags: [],
  },
];

interface ExplorePathsProps {
  /** Currently active path key (derived from mode + tags) */
  activePathKey?: string | null;
}

export function ExplorePaths({ activePathKey }: ExplorePathsProps) {
  const [, setSearchParams] = useSearchParams();

  const handlePathClick = useCallback(
    (path: PathOption) => {
      track("explore_path_click", { path: path.key, mode: path.mode }, "explore");

      setSearchParams((prev) => {
        // Set mode
        if (path.mode === "places") prev.delete("mode");
        else prev.set("mode", path.mode);

        // Set tags
        if (path.tags && path.tags.length > 0) {
          prev.set("tags", path.tags.join(","));
        } else {
          prev.delete("tags");
        }

        return prev;
      }, { replace: true });
    },
    [setSearchParams],
  );

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {PATHS.map((path) => {
        const isActive = activePathKey === path.key;
        return (
          <button
            key={path.key}
            onClick={() => handlePathClick(path)}
            className={cn(
              "group relative flex flex-col items-start gap-2 rounded-xl border p-4 text-left transition-all duration-200",
              "hover:shadow-sm hover:border-primary/30",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
              isActive
                ? "border-primary/40 bg-primary/5 shadow-sm"
                : "border-border/50 bg-card hover:bg-muted/30",
            )}
          >
            <div
              className={cn(
                "h-9 w-9 rounded-lg flex items-center justify-center transition-colors",
                isActive
                  ? "bg-primary/15 text-primary"
                  : "bg-muted/60 text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary",
              )}
            >
              <path.icon className="h-4.5 w-4.5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground leading-tight">{path.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{path.description}</p>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Derive which path is currently active based on mode + tags.
 */
export function deriveActivePathKey(mode: string, tags: string[]): string | null {
  if (mode === "do" && tags.length === 0) return "do";
  if (mode === "farms" && tags.length === 0) return "food";
  if (mode === "places" && tags.includes("natur")) return "nature";
  if (mode === "places" && tags.includes("boende")) return "stay";
  return null;
}
